import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

const DISTRICTS = [
  { id: 'eastkhasi', name: 'East Khasi Hills', homestays: 380, valleys: ['Shillong', 'Cherrapunji', 'Mawsynram', 'Laitlum Canyon'], altitude: '1,000–1,965m', highlight: true, division: 'Khasi Hills' },
  { id: 'westkhasi', name: 'West Khasi Hills', homestays: 180, valleys: ['Nongkhlaw', 'Mairang', 'Nongstoin', 'Mawphanlur'], altitude: '800–1,800m', highlight: true, division: 'Khasi Hills' },
  { id: 'swkhasi', name: 'South West Khasi', homestays: 120, valleys: ['Mawkyrwat', 'Ranikor', 'Living Root Bridges', 'Nongriat'], altitude: '200–1,500m', highlight: true, division: 'Khasi Hills' },
  { id: 'eastjaintia', name: 'East Jaintia Hills', homestays: 150, valleys: ['Dawki', 'Shnongpdeng', 'Krang Suri Falls', 'Jowai'], altitude: '300–1,600m', highlight: true, division: 'Jaintia Hills' },
  { id: 'westjaintia', name: 'West Jaintia Hills', homestays: 100, valleys: ['Nartiang', 'Thadlaskein Lake', 'Monoliths', 'Syndai'], altitude: '500–1,500m', highlight: false, division: 'Jaintia Hills' },
  { id: 'ribhoi', name: 'Ri Bhoi', homestays: 120, valleys: ['Nongpoh', 'Umiam Lake', 'Kyllang Rock', 'Orchid Lake'], altitude: '200–1,200m', highlight: true, division: 'Khasi Hills' },
  { id: 'westgaro', name: 'West Garo Hills', homestays: 130, valleys: ['Tura', 'Tura Peak', 'Nokrek NP', 'Siju Caves'], altitude: '100–1,412m', highlight: false, division: 'Garo Hills' },
  { id: 'eastgaro', name: 'East Garo Hills', homestays: 80, valleys: ['Williamnagar', 'Simsang River', 'Balpakram NP', 'Baghmara'], altitude: '100–900m', highlight: false, division: 'Garo Hills' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Meghalaya Tourism Department, from cloud-soaked Khasi hills to the Garo lowlands, digitized and discoverable.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on region map', 'Basic contact listing', 'Searchable by hill & tribe'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays editorially visited and quality-verified. Verified root-bridge stays, canyon-edge retreats, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing, direct booking, and dedicated account management. The hidden gems of the Abode of Clouds.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay from Cherrapunji\'s living root bridges to Dawki\'s crystal rivers — searchable, verified, bookable on one platform.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, monsoon-tourism trends, and festival-season occupancy insights for Meghalaya tourism planning.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy — empowering Khasi, Jaintia, and Garo communities to host the world sustainably.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification ensuring each homestay honours the matrilineal traditions, sacred groves, and living architecture of the state.' },
];

function MeghalayaMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'ribhoi': { d: 'M200 50 L300 40 L340 75 L310 110 L240 115 L195 85 Z', cx: 265, cy: 78 },
    'eastkhasi': { d: 'M195 85 L240 115 L310 110 L330 145 L280 175 L210 170 L170 135 Z', cx: 250, cy: 135 },
    'westkhasi': { d: 'M60 90 L150 70 L195 85 L170 135 L130 160 L70 150 L45 120 Z', cx: 120, cy: 115 },
    'swkhasi': { d: 'M70 150 L130 160 L170 195 L140 230 L80 225 L50 195 Z', cx: 108, cy: 192 },
    'eastjaintia': { d: 'M330 145 L420 130 L450 165 L430 200 L370 210 L330 190 L280 175 Z', cx: 375, cy: 170 },
    'westjaintia': { d: 'M210 170 L280 175 L330 190 L310 225 L250 235 L200 215 Z', cx: 265, cy: 200 },
    'westgaro': { d: 'M30 180 L50 195 L80 225 L70 270 L120 290 L80 310 L20 295 L10 240 Z', cx: 55, cy: 255 },
    'eastgaro': { d: 'M120 290 L200 270 L250 290 L240 330 L170 335 L100 320 L80 310 Z', cx: 170, cy: 310 },
  };

  return (
    <svg viewBox="0 0 500 360" className="w-full max-w-2xl mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id} onMouseEnter={() => setActiveDistrict(id)} onMouseLeave={() => setActiveDistrict(null)} style={{ cursor: 'pointer' }}>
            <path d={path.d}
              fill={isActive ? '#5FB8C2' : isHighlight ? 'rgba(95,184,194,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#8FD8E0' : 'rgba(255,255,255,0.3)'} strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }} />
            <text x={path.cx} y={path.cy} textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'} fontSize={isActive ? 10 : 8}
              fontFamily="Georgia, serif" style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}>
              {district?.name}
            </text>
            {isActive && (
              <text x={path.cx} y={path.cy + 14} textAnchor="middle" dominantBaseline="middle"
                fill="#8FD8E0" fontSize={8} fontFamily="Georgia, serif" style={{ pointerEvents: 'none' }}>
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function MeghalayaStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="meghalaya-state-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="meghalaya" />
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1637043765564-a071ff91a09f?w=1920&q=80"
            alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Meghalaya &middot; Abode of Clouds</span>
          </div>
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>
          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 12 Districts &middot; One Platform
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Where trees grow into bridges,<br />and the rain writes the history
          </h2>
        </div>
      </section>

      {/* Story */}
      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#5FB8C2] text-[10px] tracking-[0.5em] uppercase mb-6">The Living Architecture</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            In the wettest place on Earth,<br />
            the roots hold everything together.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            In Nongriat, Khasi villagers have trained the roots of rubber fig trees across rivers 
            for centuries — bridges that grow stronger with every monsoon, some over 500 years old. 
            In Cherrapunji, which once held the world rainfall record, waterfalls plunge into limestone 
            gorges while orchids cling to every surface. At Dawki, the Umngot river runs so clear that 
            boats appear to float on glass above a turquoise bed. In Mawlynnong, Asia's cleanest village, 
            bamboo dustbins line paths through living-root bridges and sacred groves. The Khasi people 
            trace lineage through mothers — the youngest daughter inherits the ancestral home. In the 
            Garo hills, Balpakram's canyons hold the spirits of the departed in Garo mythology. This 
            is a state where nature and culture are not separate things — they are one continuous, 
            breathing architecture.
          </p>
        </div>
      </section>

      {/* Living Map */}
      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#5FB8C2] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Khasi peaks to Garo valleys. Three tribes.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <MeghalayaMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} />
            </div>
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#5FB8C2] text-[10px] tracking-[0.4em] uppercase">Region</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division}</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.name}</h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.homestays}</span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-white/40" />
                      <span className="text-white/60 text-sm">{activeData.altitude}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeData.valleys.map(v => (
                        <span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">{v}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a region to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer"
                        onMouseEnter={() => setActiveDistrict(d.id)}>
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{d.homestays}</p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Meghalaya</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{TOTAL_HOMESTAYS.toLocaleString()} homestays</span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#5FB8C2] to-[#8FD8E0] rounded-full" style={{ width: '100%', animation: 'expandBar 2s ease-out' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tier Funnel */}
      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#5FB8C2] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Three tiers. One ecosystem.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">Every homestay begins as Registered. Through quality verification and partnership, they rise — from Silver to Gold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name} className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group" style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}>
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{i + 1}</div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{tier.name}</h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} />
                      <span className="text-white/55 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                {i < 2 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ArrowRight className="w-5 h-5 text-white/10" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2G Section */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#5FB8C2] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br /><span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">GoHimalayan serves as the technology and marketing partner for state tourism departments — bringing every licensed homestay across the Khasi, Jaintia, and Garo hills online and discoverable.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#5FB8C2] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{prop.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-[#5FB8C2]/20 rounded-xl p-10 sm:p-14 text-center" style={{ background: 'linear-gradient(160deg, rgba(95,184,194,0.05), transparent)' }}>
            <p className="text-[#5FB8C2] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Built to scale. Designed to empower.</h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#5FB8C2]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#5FB8C2] text-xs">1</span></div>
                <p className="text-white/60 text-sm">Meghalaya shares its licensed homestay registry — GoHimalayan digitizes and maps every property across 12 districts and three tribal regions</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#5FB8C2]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#5FB8C2] text-xs">2</span></div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into matrilineal Khasi villages, cave systems, and living-root-bridge communities</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#5FB8C2]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#5FB8C2] text-xs">3</span></div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, monsoon-tourism analytics, and sustainable eco-tourism planning dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Meghalaya."
              className="inline-flex items-center gap-3 bg-[#5FB8C2] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#7dccd4] transition-colors"
              data-testid="b2g-cta" target="_blank" rel="noopener noreferrer">
              Begin Conversation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#5FB8C2" />
      <style>{`@keyframes expandBar { from { width: 0; } to { width: 100%; } }`}</style>
    </div>
  );
}
